import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store';
import { api, getApiErrorMessage } from '../lib/api';
import { DASHBOARD_WIDGETS, DashboardWidgetDef } from '../constants/dashboardWidgets';

interface HomeDashboardData {
  kpis: {
    openTasks: { value: number; netToday: number };
    claimedToday: { value: number; byMembers: number };
    slaAttainmentPct: { value: number | null; deltaPct: number | null; windowDays: number };
    avgResolutionMinutes: { value: number | null; deltaMinutes: number | null; windowDays: number };
  };
  myQueue: Array<{ id: string; key: string; title: string; status: string; created_at: string }>;
  workloadByStatus: { todo: number; claimed: number; completed: number };
  recentActivity: Array<{ text: string; when: string; type: string }>;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [data, setData] = useState<HomeDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [widgetIds, setWidgetIds] = useState<string[]>([]);
  const [widgetValues, setWidgetValues] = useState<Record<string, number | null> | null>(null);
  const [widgetsLoading, setWidgetsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchHome = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/dashboard/home');
        if (!cancelled) {
          setData((res.data as any)?.data ?? null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(getApiErrorMessage(err, t('errors.general')));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const fetchWidgets = async () => {
      setWidgetsLoading(true);
      try {
        const [prefsRes, valuesRes] = await Promise.all([
          api.get('/dashboard/widget-preferences'),
          api.get('/dashboard/widgets')
        ]);
        if (!cancelled) {
          setWidgetIds((prefsRes.data as any)?.data?.widget_ids ?? []);
          setWidgetValues((valuesRes.data as any)?.data ?? null);
        }
      } catch {
        // Widget catalog is a nice-to-have on top of the fixed KPI row
        // above — a failure here shouldn't block the rest of the page.
      } finally {
        if (!cancelled) setWidgetsLoading(false);
      }
    };

    fetchHome();
    fetchWidgets();
    return () => { cancelled = true; };
  }, [t]);

  const greetingKey = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'dashboard.greeting.morning';
    if (hour < 18) return 'dashboard.greeting.afternoon';
    return 'dashboard.greeting.evening';
  })();

  const formatRelativeTime = (dateStr: string): string => {
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return t('common.justNow');
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  const formatMinutes = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest > 0 ? `${hours}h ${rest}m` : `${hours}h`;
  };

  const formatWidgetValue = (widget: DashboardWidgetDef, value: number | null): string => {
    if (value === null || value === undefined) return '–';
    if (widget.format === 'percent') return `${value}%`;
    if (widget.format === 'minutes') return formatMinutes(value);
    return String(value);
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, string> = {
      todo: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      claimed: 'bg-yellow-100 text-yellow-800 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-1 dark:ring-inset dark:ring-amber-500/30',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    };
    const labelKey = `tasks.detail.status.${status === 'todo' ? 'todo' : status === 'claimed' ? 'claimed' : 'completed'}`;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config[status] || config.todo}`}>
        {t(labelKey)}
      </span>
    );
  };

  const getStatusBarColor = (status: string): string => {
    const colors: Record<string, string> = {
      todo: 'bg-gray-400 dark:bg-gray-500',
      claimed: 'bg-amber-500',
      completed: 'bg-green-500'
    };
    return colors[status] || colors.todo;
  };

  const statusOrder: Array<'todo' | 'claimed' | 'completed'> = ['todo', 'claimed', 'completed'];
  const workloadMax = data ? Math.max(1, ...statusOrder.map((s) => data.workloadByStatus[s])) : 1;
  const selectedWidgets = widgetIds
    .map((id) => DASHBOARD_WIDGETS.find((w) => w.id === id))
    .filter((w): w is DashboardWidgetDef => !!w);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {t(greetingKey)}{user?.name ? `, ${user.name}` : ''}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard.subtitle')}</p>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Widgets — user-customizable (picked in Settings), each an
          independent card. No wrapping "My widgets" box: this row simply
          IS the dashboard's stat row now (it replaced the old fixed one). */}
      <div className="flex items-center">
        <Link to="/settings?section=homeCustomization" className="ml-auto text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
          {t('dashboard.widgetsSection.customize')}
        </Link>
      </div>
      {!widgetsLoading && selectedWidgets.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard.widgetsSection.empty')}</p>
      )}
      {selectedWidgets.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 items-start">
          {selectedWidgets.map((w) => {
            if (w.format === 'workload') {
              return (
                <section key={w.id} className="col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 px-4 py-3">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t(w.labelKey)}</span>
                  <div className="flex flex-col gap-2.5 mt-3">
                    {statusOrder.map((s) => (
                      <div key={s} className="flex flex-col gap-1">
                        <div className="flex text-xs text-gray-500 dark:text-gray-400">
                          <span>{t(`tasks.detail.status.${s}`)}</span>
                          <span className="ml-auto font-mono text-gray-700 dark:text-gray-300">{loading ? '…' : data?.workloadByStatus[s] ?? 0}</span>
                        </div>
                        <div className="h-1.5 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden">
                          <div
                            className={`h-full ${getStatusBarColor(s)}`}
                            style={{ width: data ? `${(data.workloadByStatus[s] / workloadMax) * 100}%` : '0%' }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            }

            if (w.format === 'activity') {
              return (
                <section key={w.id} className="col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 px-4 py-3">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t(w.labelKey)}</span>
                  <div className="flex flex-col gap-2.5 mt-3">
                    {!loading && (data?.recentActivity.length ?? 0) === 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">{t('dashboard.activity.empty')}</span>
                    )}
                    {data?.recentActivity.map((a, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${a.type === 'created' ? 'bg-blue-500' : 'bg-gray-400 dark:bg-gray-500'}`} />
                        <div className="min-w-0">
                          <p className="text-xs text-gray-700 dark:text-gray-300">{a.text}</p>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">{formatRelativeTime(a.when)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            }

            const value = widgetValues ? widgetValues[w.id] : null;
            const card = (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 px-4 py-3 h-full">
                <span className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">{t(w.labelKey)}</span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {widgetsLoading ? '…' : formatWidgetValue(w, value)}
                  </span>
                </div>
              </div>
            );
            return w.link ? (
              <Link key={w.id} to={w.link} className="block hover:opacity-90 transition-opacity">{card}</Link>
            ) : (
              <div key={w.id}>{card}</div>
            );
          })}
        </div>
      )}

      {/* My queue — fixed, not part of the customizable widget set */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="flex items-center px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('dashboard.myQueue.title')}</span>
          <Link to="/tasks" className="ml-auto text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
            {t('dashboard.myQueue.openTaskList')} →
          </Link>
        </div>
        {loading && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">{t('common.loading')}</div>
        )}
        {!loading && (data?.myQueue.length ?? 0) === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">{t('dashboard.myQueue.empty')}</div>
        )}
        {!loading && data && data.myQueue.length > 0 && (
          <div className="max-h-[380px] overflow-y-auto">
            {data.myQueue.map((q) => (
              <Link
                key={q.id}
                to={`/tasks/${q.id}`}
                className="grid grid-cols-[70px_1fr_auto_50px] items-center gap-2 px-4 py-2.5 border-b border-gray-50 dark:border-gray-700/60 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
              >
                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{q.key}</span>
                <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{q.title}</span>
                {getStatusBadge(q.status)}
                <span className="text-xs text-gray-500 dark:text-gray-400 text-right">{formatRelativeTime(q.created_at)}</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Dashboard;
